import { useState, type DragEvent } from 'react';
import {
  FRAME_COST_MICROS,
  type Garment,
  type GarmentCategory,
  type GarmentViewKind,
  type GhostSide,
} from '@studio/shared';

import { assetUrl } from '../lib/api';
import { capitalise } from '../lib/appearance';
import { useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { CATEGORIES, DEFAULT_LAYER, MAX_DETAILS, useGarments, wantsDetailPhoto } from '../lib/useGarments';
import { CaptureNotes } from './CaptureNotes';
import { DetailNote } from './GarmentUploader';
import { GarmentExample } from './GarmentExample';
import { ArrowRightIcon, CheckIcon, PlusIcon, TrashIcon } from './Icons';

/** The sides of a piece, in the order they are asked for. Only the front is needed. */
const VIEWS: GarmentViewKind[] = ['front', 'back', 'side', 'detail', 'label'];
/** The guide's steps: the photographs, then the ghost photographs made from them. */
type Step = GarmentViewKind | 'ghost';
const STEPS: Step[] = [...VIEWS, 'ghost'];

/** Which side photograph shows which profile. One without a side, from before there were two, is the left. */
function profiles(garment: Garment) {
  const sides = (garment.views ?? []).filter((v) => v.kind === 'side');
  return {
    left: sides.find((v) => v.side === 'left') ?? sides.find((v) => !v.side),
    right: sides.find((v) => v.side === 'right'),
  };
}

/** The sides a piece has photographs of, each of which gets a ghost photograph. */
function ghostSides(garment: Garment): GhostSide[] {
  const { left, right } = profiles(garment);
  const back = (garment.views ?? []).some((v) => v.kind === 'back');
  return [
    'front',
    ...(back ? (['back'] as const) : []),
    ...(left ? (['left'] as const) : []),
    ...(right ? (['right'] as const) : []),
  ];
}

/** Which side a ghost photograph shows. One from before profiles had sides is the left. */
const sideOfGhost = (g: NonNullable<Garment['ghosts']>[number]): GhostSide =>
  g.of === 'side' ? (g.side ?? 'left') : g.of;

/** Which sides of a piece are on file. The front always is: it is how a piece begins. */
function onFile(garment: Garment): Set<GarmentViewKind> {
  return new Set<GarmentViewKind>(['front', ...(garment.views ?? []).map((v) => v.kind)]);
}

interface Props {
  garments: Garment[];
  onChange: (garments: Garment[]) => void;
}

/**
 * The clothes of a roll, one photograph at a time.
 *
 * The pieces of the look down the side; for the one in hand, its sides in
 * order — front, back, side, close-ups, label — each with the photograph to
 * take beside the place to put it. Only the front is required; every other
 * side can be passed, and the sentence at the foot says what the studio does
 * without it.
 */
export function ClothesGuide({ garments, onChange }: Props) {
  const t = useT();
  const g = useGarments(garments, onChange);
  const [selectedId, setSelectedId] = useState<string | null>(() => garments[0]?.id ?? null);
  const [view, setView] = useState<Step>('front');
  const money = useMoney();
  const [closet, setCloset] = useState(false);
  /** The kind of the next new piece: asked first, so the example and the layer fit it. */
  const [kind, setKind] = useState<GarmentCategory | null>(null);

  // A piece taken off the roll leaves the guide on a new piece, not on nothing.
  const piece = garments.find((p) => p.id === selectedId);
  const step = piece ? view : 'front';
  const have: Set<Step> = piece ? onFile(piece) : new Set<Step>();
  if (piece?.ghosts?.length) have.add('ghost');
  const at = STEPS.indexOf(step);
  const name = (kind: Step) => t(`dress.view.${kind}`);
  /** The sides of this piece whose ghost photograph is being made. */
  const making = piece
    ? g.ghosting.filter((key) => key.startsWith(`${piece.id}:`)).map((key) => key.split(':')[1] as GhostSide)
    : [];

  /** The photographs are in: on to the ghost photographs, any not yet made started now. */
  const toGhosts = (p: Garment) => {
    setView('ghost');
    const made = new Set((p.ghosts ?? []).map(sideOfGhost));
    const missing = ghostSides(p).filter((side) => !made.has(side));
    if (missing.length) void g.makeGhosts(p.id, missing);
  };

  const open = (id: string | null) => {
    setSelectedId(id);
    setView('front');
    setCloset(false);
    setKind(null);
  };

  /** A new piece begins with its front, and the guide moves on to its back. */
  const addPieces = async (files: File[] | null) => {
    const added = await g.upload(files, kind ?? undefined);
    // Each front's ghost photograph is started now, in the background, while the rest is photographed.
    for (const p of added) void g.makeGhosts(p.id, ['front']);
    const first = added[0];
    if (first) {
      setSelectedId(first.id);
      setView('back');
      setKind(null);
    }
  };

  const takeOff = (id: string) => {
    const rest = garments.filter((p) => p.id !== id);
    g.remove(id);
    open(rest[0]?.id ?? null);
  };

  const details = piece ? (piece.views ?? []).filter((v) => v.kind === 'detail') : [];
  const single =
    piece && (step === 'back' || step === 'label') ? piece.views?.find((v) => v.kind === step) : undefined;

  const hint = !piece
    ? t('dress.hint.new')
    : step === 'front'
      ? t(wantsDetailPhoto(piece) ? 'wardrobe.detailHintShort' : 'dress.hint.front')
      : t(`dress.hint.${step}`);

  return (
    <div className="dress">
      <nav className="dress__rail" aria-label={t('dress.look')}>
        <span className="dress__railTitle">{t('dress.look')}</span>
        {garments.map((p) => {
          const n = onFile(p).size;
          const on = !closet && p.id === piece?.id;
          return (
            <button
              key={p.id}
              type="button"
              className={`dress__piece${on ? ' is-on' : ''}`}
              aria-current={on ? 'true' : undefined}
              onClick={() => open(p.id)}
            >
              <img src={assetUrl(p.assetId)} alt="" />
              <span>
                <b>{capitalise(p.label || t(`enum.category.${p.category}`))}</b>
                <small>
                  {n === VIEWS.length
                    ? t('dress.complete')
                    : t(n === 1 ? 'dress.viewOf' : 'dress.viewsOf', { n, of: VIEWS.length })}
                </small>
              </span>
            </button>
          );
        })}
        <button
          type="button"
          className={`dress__piece dress__piece--add${!closet && !piece ? ' is-on' : ''}`}
          onClick={() => open(null)}
        >
          <span className="dress__addIcon" aria-hidden="true">
            <PlusIcon />
          </span>
          <b>{t('dress.addPiece')}</b>
        </button>
        <button
          type="button"
          className={`dress__closetBtn${closet ? ' is-on' : ''}`}
          aria-pressed={closet}
          onClick={() => {
            setCloset((was) => !was);
            void g.openWardrobe();
          }}
        >
          {t('wardrobe.fromWardrobe')}
        </button>
      </nav>

      <div className="dress__main">
        {g.notes.length > 0 && <CaptureNotes notes={g.notes} onDismiss={g.clearNotes} />}
        {g.error && <div className="banner banner--error">{g.error}</div>}

        {closet ? (
          <Closet
            wardrobe={g.wardrobe}
            worn={garments}
            onWear={g.wear}
            onDestroy={(p) => void g.destroy(p)}
            onClose={() => setCloset(false)}
          />
        ) : !piece && !kind ? (
          <KindPicker onPick={setKind} />
        ) : (
          <>
            <div className="dress__head">
              <h2 className="dress__title">
                {piece
                  ? capitalise(piece.label || t(`enum.category.${piece.category}`))
                  : t('dress.newKind', { kind: t(`enum.category.${kind}`) })}
                <span> · {t(`dress.the.${step}`)}</span>
              </h2>
              {piece ? (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => takeOff(piece.id)}>
                  {t('dress.removePiece')}
                </button>
              ) : (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setKind(null)}>
                  {t('dress.changeKind')}
                </button>
              )}
            </div>

            <ol className="dress__steps" aria-label={t('dress.views')}>
              {STEPS.map((kind) => {
                const done = have.has(kind);
                const now = kind === step;
                return (
                  <li key={kind}>
                    <button
                      type="button"
                      className={`dress__step${done ? ' is-done' : ''}${now ? ' is-now' : ''}`}
                      aria-current={now ? 'step' : undefined}
                      disabled={!piece}
                      onClick={() => setView(kind)}
                    >
                      <span className="dress__bar" aria-hidden="true" />
                      <span className="dress__stepName">
                        {name(kind)}
                        {done && <CheckIcon />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            {piece && step === 'ghost' ? (
              <GhostPane
                piece={piece}
                sides={ghostSides(piece)}
                making={making}
                cost={money.cost(ghostSides(piece).length * FRAME_COST_MICROS.strong)}
                eachCost={money.cost(FRAME_COST_MICROS.strong)}
                onRedo={() => void g.makeGhosts(piece.id, ghostSides(piece))}
                onRetouch={(side, correction) => void g.makeGhosts(piece.id, [side], { correction })}
              />
            ) : (
              <div className="dress__panes">
                <figure className="dress__example">
                  <div className="dress__frame">
                    <GarmentExample
                      view={step as GarmentViewKind}
                      category={piece?.category ?? kind ?? undefined}
                    />
                    <span className="dress__tag">{t('wardrobe.exampleTag')}</span>
                  </div>
                  <figcaption>{t(`wardrobe.example.${step}`)}</figcaption>
                </figure>

                <div className="dress__yours">
                  {!piece ? (
                    <DropPane
                      title={t('dress.yours.new')}
                      multiple
                      busy={g.busy}
                      onFiles={(files) => void addPieces(files)}
                    />
                  ) : step === 'front' ? (
                    <div className="dress__frame dress__photo">
                      <img src={assetUrl(piece.assetId)} alt={piece.label} />
                      <span className="dress__chip">
                        <CheckIcon /> {t('dress.added')}
                      </span>
                    </div>
                  ) : step === 'detail' ? (
                    details.length === 0 ? (
                      <DropPane
                        title={t('dress.yours.detail')}
                        multiple
                        busy={g.busy}
                        onFiles={(files) => void g.addView(piece.id, 'detail', files)}
                      />
                    ) : (
                      <ul className="dress__details" aria-label={t('wardrobe.details')}>
                        {details.map((v, i) => (
                          <li key={v.assetId} className="dress__detail">
                            <span className="dress__detailPhoto">
                              <img src={assetUrl(v.assetId)} alt="" />
                              <button
                                type="button"
                                className="dress__drop"
                                onClick={() => void g.removeView(piece.id, v.assetId)}
                                aria-label={t('wardrobe.removeView', { kind: t('wardrobe.view.detail') })}
                              >
                                ×
                              </button>
                            </span>
                            <DetailNote
                              note={v.note ?? ''}
                              label={t('wardrobe.detailWhereN', { n: i + 1 })}
                              onSave={(note) => void g.noteView(piece.id, v.assetId, note)}
                            />
                          </li>
                        ))}
                        {details.length < MAX_DETAILS && (
                          <li className="dress__detail">
                            <DropPane
                              compact
                              title={t('dress.addDetail')}
                              multiple
                              busy={g.busy}
                              onFiles={(files) => void g.addView(piece.id, 'detail', files)}
                            />
                          </li>
                        )}
                      </ul>
                    )
                  ) : step === 'side' ? (
                    /* Two profiles, either of which can be left out. */
                    <div className="dress__sides">
                      {(['left', 'right'] as const).map((facing) => {
                        const photo = profiles(piece)[facing];
                        return (
                          <div key={facing} className="dress__sideSlot">
                            <span className="dress__sideName">{t(`dress.side.${facing}`)}</span>
                            {photo ? (
                              <div className="dress__frame dress__photo">
                                <img src={assetUrl(photo.assetId)} alt={t(`dress.side.${facing}`)} />
                                <button
                                  type="button"
                                  className="dress__drop"
                                  onClick={() => void g.removeView(piece.id, photo.assetId)}
                                  aria-label={t('wardrobe.removeView', { kind: t(`dress.side.${facing}`) })}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <DropPane
                                title={t(`dress.yours.side.${facing}`)}
                                busy={g.busy}
                                onFiles={(files) =>
                                  void g
                                    .addView(piece.id, 'side', files, facing)
                                    .then(() => g.makeGhosts(piece.id, [facing]))
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : single ? (
                    <div className="dress__frame dress__photo">
                      <img src={assetUrl(single.assetId)} alt={name(step)} />
                      <span className="dress__chip">
                        <CheckIcon /> {t('dress.added')}
                      </span>
                      <button
                        type="button"
                        className="dress__drop"
                        onClick={() => void g.removeView(piece.id, single.assetId)}
                        aria-label={t('wardrobe.removeView', { kind: t(`wardrobe.view.${step}`) })}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <DropPane
                      title={t(`dress.yours.${step}`)}
                      busy={g.busy}
                      onFiles={(files) =>
                        void g.addView(piece.id, step as GarmentViewKind, files).then(() => {
                          // The back's ghost photograph is started as soon as the back is in.
                          if (step === 'back') return g.makeGhosts(piece.id, ['back']);
                        })
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {piece && step === 'front' && (
              <PieceFields piece={piece} onPatch={(c) => void g.patch(piece.id, c)} />
            )}

            <div className="dress__foot">
              {at > 0 && (
                <button type="button" className="btn btn--ghost" onClick={() => setView(STEPS[at - 1]!)}>
                  ← {name(STEPS[at - 1]!)}
                </button>
              )}
              <span className="dress__hint">{hint}</span>
              {piece &&
                (step === 'label' ? (
                  <button type="button" className="btn btn--primary" onClick={() => toGhosts(piece)}>
                    {name('ghost')}
                    <ArrowRightIcon />
                  </button>
                ) : at < STEPS.length - 1 ? (
                  have.has(step) ? (
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => setView(STEPS[at + 1]!)}
                    >
                      {name(STEPS[at + 1]!)}
                      <ArrowRightIcon />
                    </button>
                  ) : (
                    <button type="button" className="btn btn--ghost" onClick={() => setView(STEPS[at + 1]!)}>
                      {t('dress.skip')}
                      <ArrowRightIcon />
                    </button>
                  )
                ) : (
                  <button type="button" className="btn btn--primary" onClick={() => open(null)}>
                    <CheckIcon />
                    {t('dress.done')}
                  </button>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The first question about a new piece: what kind it is. Asked before the
 * photograph so the example beside the drop area shows that kind — a pair of
 * jeans for trousers, trainers for shoes — and the piece gets its layer.
 */
function KindPicker({ onPick }: { onPick: (kind: GarmentCategory) => void }) {
  const t = useT();
  return (
    <>
      <div className="dress__head">
        <h2 className="dress__title">
          {t('dress.newPiece')}
          <span> · {t('dress.the.kind')}</span>
        </h2>
      </div>
      <div className="dress__kinds" role="group" aria-label={t('wardrobe.kind')}>
        {CATEGORIES.map((c) => (
          <button key={c} type="button" className="dress__kind" onClick={() => onPick(c)}>
            {t(`enum.category.${c}`)}
            <ArrowRightIcon />
          </button>
        ))}
      </div>
      <p className="dress__hint">{t('dress.hint.kind')}</p>
    </>
  );
}

/**
 * The ghost photographs of a piece, one tile per side photographed: made,
 * or still being made in the background — with what a new set costs.
 */
function GhostPane({
  piece,
  sides,
  making,
  cost,
  eachCost,
  onRedo,
  onRetouch,
}: {
  piece: Garment;
  sides: GhostSide[];
  making: GhostSide[];
  cost: string;
  eachCost: string;
  onRedo: () => void;
  /** Redo one ghost from the current one, with what to change. */
  onRetouch: (side: GhostSide, correction: string) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState<GhostSide | null>(null);
  const [correction, setCorrection] = useState('');
  const caption = (side: GhostSide) =>
    side === 'left' || side === 'right' ? t(`dress.side.${side}`) : t(`dress.view.${side}`);
  const retouch = (side: GhostSide) => {
    onRetouch(side, correction.trim());
    setEditing(null);
    setCorrection('');
  };
  return (
    <div className="dress__ghosts">
      <ul className="dress__ghostGrid">
        {sides.map((side) => {
          const ghost = (piece.ghosts ?? []).find((g) => sideOfGhost(g) === side);
          const busy = making.includes(side);
          return (
            <li key={side}>
              <span className={`dress__ghostPhoto${busy ? ' is-making' : ''}`}>
                {ghost && !busy ? (
                  <img src={assetUrl(ghost.assetId)} alt={caption(side)} />
                ) : (
                  <span className="dress__ghostWait" role="status">
                    {busy && <span className="spinner" />}
                    <small>{t(busy ? 'dress.ghostMaking' : 'dress.ghostWaiting')}</small>
                  </span>
                )}
              </span>
              <span className="dress__ghostName">{caption(side)}</span>
              {ghost && !busy && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setEditing(side);
                    setCorrection('');
                  }}
                >
                  {t('dress.ghostRetouch')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {editing && (
        <form
          className="dress__ghostFix"
          onSubmit={(event) => {
            event.preventDefault();
            retouch(editing);
          }}
        >
          <label className="dress__ghostFixLabel" htmlFor="ghost-fix">
            {t('dress.ghostWhatLabel', { side: caption(editing) })}
          </label>
          <input
            id="ghost-fix"
            className="desc__pill desc__input"
            value={correction}
            // Focused on open: the whole point of pressing "redo" is to say what to change.
            autoFocus
            maxLength={400}
            placeholder={t('dress.ghostWhat')}
            onChange={(event) => setCorrection(event.target.value)}
          />
          <span className="dress__ghostFixRow">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn--primary btn--sm">
              {t('dress.ghostRetouchGo', { cost: eachCost })}
            </button>
          </span>
        </form>
      )}
      <button type="button" className="btn btn--ghost btn--sm" disabled={making.length > 0} onClick={onRedo}>
        {t('dress.ghostRedo', { cost })}
      </button>
    </div>
  );
}

/** What the piece is called, what kind it is, and how close to the skin it sits. */
function PieceFields({ piece, onPatch }: { piece: Garment; onPatch: (changes: Partial<Garment>) => void }) {
  const t = useT();
  return (
    <div className="dress__fields">
      <input
        className="desc__pill desc__input dress__name"
        value={piece.label}
        placeholder={t('wardrobe.what')}
        aria-label={t('wardrobe.what')}
        onChange={(event) => onPatch({ label: event.target.value })}
      />
      <select
        className="desc__pill"
        aria-label={t('wardrobe.kind')}
        value={piece.category}
        onChange={(event) => {
          const category = event.target.value as GarmentCategory;
          onPatch({ category, layer: DEFAULT_LAYER[category] });
        }}
      >
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {t(`enum.category.${c}`)}
          </option>
        ))}
      </select>
      <select
        className="desc__pill"
        aria-label={t('wardrobe.layer')}
        value={piece.layer}
        onChange={(event) => onPatch({ layer: Number(event.target.value) })}
      >
        <option value={1}>{capitalise(t('wardrobe.layer1'))}</option>
        <option value={5}>{capitalise(t('wardrobe.layer5'))}</option>
        <option value={10}>{capitalise(t('wardrobe.layer10'))}</option>
        <option value={12}>{capitalise(t('wardrobe.layer12'))}</option>
      </select>
      <input
        className="desc__pill desc__input dress__notes"
        value={piece.notes}
        placeholder={t('wardrobe.notesShort')}
        aria-label={t('wardrobe.notes')}
        onChange={(event) => onPatch({ notes: event.target.value })}
      />
    </div>
  );
}

/**
 * Where a photograph goes: pressed to choose one, or dropped on.
 *
 * A label around its own input, so the whole area is the button and its
 * words are the input's name.
 */
function DropPane({
  title,
  multiple = false,
  compact = false,
  busy,
  onFiles,
}: {
  title: string;
  multiple?: boolean;
  compact?: boolean;
  busy: boolean;
  onFiles: (files: File[] | null) => void;
}) {
  const t = useT();
  // Counted rather than flagged: crossing a child fires a leave on the parent.
  const [depth, setDepth] = useState(0);
  const over = depth > 0;
  const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');
  return (
    <label
      className={`dress__dropzone${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}${compact ? ' dress__dropzone--compact' : ''}`}
      onDragEnter={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        setDepth((d) => d + 1);
      }}
      onDragOver={(event) => {
        if (!carriesFiles(event)) return;
        // Without this the browser opens the photograph in place of the app.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(event) => {
        if (!carriesFiles(event)) return;
        setDepth((d) => Math.max(0, d - 1));
      }}
      onDrop={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        setDepth(0);
        if (!busy) onFiles(Array.from(event.dataTransfer.files));
      }}
    >
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple={multiple}
        className="sr-only"
        disabled={busy}
        onChange={(event) => {
          // Copied before the reset below empties the list it came in.
          const files = event.target.files ? Array.from(event.target.files) : null;
          event.target.value = '';
          onFiles(files);
        }}
      />
      <span className="dress__plus" aria-hidden="true">
        {busy ? <span className="spinner" /> : <PlusIcon />}
      </span>
      <b>{busy ? t('wardrobe.uploading') : over ? t('wardrobe.dropHere') : title}</b>
      {!compact && !busy && !over && (
        <small>{t(multiple ? 'dress.dropOrPickMany' : 'dress.dropOrPick')}</small>
      )}
    </label>
  );
}

/** The wardrobe: every piece already photographed, worn on this roll with one press. */
function Closet({
  wardrobe,
  worn,
  onWear,
  onDestroy,
  onClose,
}: {
  wardrobe: Garment[] | undefined;
  worn: Garment[];
  onWear: (piece: Garment) => void;
  onDestroy: (piece: Garment) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <>
      <div className="dress__head">
        <h2 className="dress__title">{t('dress.closet')}</h2>
        <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
          {t('dress.closetDone')}
        </button>
      </div>
      {!wardrobe ? (
        <p className="faint">{t('wardrobe.loading')}</p>
      ) : wardrobe.length === 0 ? (
        <p className="faint">{t('wardrobe.empty')}</p>
      ) : (
        <div className="closet">
          {wardrobe.map((piece) => {
            const on = worn.some((g) => g.id === piece.id);
            return (
              <span key={piece.id} className={`closet__item ${on ? 'is-on' : ''}`}>
                <button
                  type="button"
                  className="closet__pick"
                  onClick={() => onWear(piece)}
                  aria-pressed={on}
                >
                  <span className="closet__photo">
                    <img src={assetUrl(piece.assetId)} alt="" loading="lazy" decoding="async" />
                    {on && (
                      <span className="closet__check" aria-hidden="true">
                        <CheckIcon />
                      </span>
                    )}
                  </span>
                  <span className="closet__label">{piece.label}</span>
                </button>
                <button
                  type="button"
                  className="closet__drop"
                  onClick={() => onDestroy(piece)}
                  aria-label={t('wardrobe.deleteForever', { label: piece.label })}
                >
                  <TrashIcon />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}
